import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFiltersContext } from '../../context/FiltersContext.tsx';
import type { FilterCriteria } from '../types.ts';

const ALWAYS_PRESERVED_FILTERS = ['phrase'];

export const ClearFiltersButton = () => {
  const { t } = useTranslation();
  const {
    criteria: filterCriteria,
    onFiltersChange,
    disabledFilters,
    isFilteringDisabled,
    hideClearFiltersButton,
    defaultFilters,
    filtersConfig,
  } = useFiltersContext<FilterCriteria>();

  const activeFilters = filterCriteria?.filters ?? {};

  const nonClearableFilterKeys = useMemo(
    () => (filtersConfig ?? []).filter(filter => filter.clearable === false).map(filter => filter.key),
    [filtersConfig]
  );

  const preservedFilters = useMemo(
    () => [...disabledFilters, ...ALWAYS_PRESERVED_FILTERS, ...nonClearableFilterKeys],
    [disabledFilters, nonClearableFilterKeys]
  );

  const shouldShowClearButton = useMemo(() => {
    return Object.entries(activeFilters).some(([key, value]) => {
      if (preservedFilters.includes(key)) return false;
      if (value === undefined || value === '') return false;
      // plain objects (e.g. byPhrase) are internal/derived, not user-selected filters
      if (typeof value === 'object' && !Array.isArray(value)) return false;
      return true;
    });
  }, [activeFilters, preservedFilters]);

  const handleClearFilters = useCallback(() => {
    const clearedExceptPreserved = Object.fromEntries(
      Object.entries(activeFilters).filter(([key]) => preservedFilters.includes(key))
    );

    const updatedFilters = {
      ...(defaultFilters ?? {}),
      ...clearedExceptPreserved,
    };

    onFiltersChange(updatedFilters);
  }, [activeFilters, defaultFilters, preservedFilters, onFiltersChange]);

  if (isFilteringDisabled || !shouldShowClearButton || hideClearFiltersButton) {
    return null;
  }

  return (
    <Button data-test="clear-all-filters" sx={{ color: 'textSecondary' }} variant="text" onClick={handleClearFilters}>
      {t('Clear filters')}
    </Button>
  );
};
